<script setup lang="ts">
import { useBrowserStore } from '@apps/main/new-desktop/core/stores/browserStore';

import { AppSwiperGallery, AppButton, AppRawButton } from '@libs/ui';

import {
  useCoreStore,
  useStatsigEventsStore,
} from '../../../../../core/stores';
import type {
  ActivityProductCardData,
  ActivitySegmentContext,
} from '../../../models';
import ActivityProductCardLayout from '../ActivityProductCardLayout.vue';
import ImgProductCardLayout from '../ImgProductCardLayout.vue';
import ProductCardFooter from '../ProductCardFooter.vue';
import DateCardTag from '../DateCardTag.vue';
import TripOverviewTrashIconButton from '../../TripOverviewTrashIconButton.vue';

import ActivityProductCardContent from './ActivityProductCardContent.vue';

interface ActivityProductCardProps {
  date: string;
  productCardData: ActivityProductCardData;
  productCardContext: ActivitySegmentContext;
  printMode: boolean;
  shouldHideProductLink: boolean;
}

const props = defineProps<ActivityProductCardProps>();

interface ActivityProductCardEmits {
  (event: 'show-details'): void;
  (event: 'show-details-tickets'): void;
  (event: 'change-product'): void;
  (event: 'remove'): void;
}

const emit = defineEmits<ActivityProductCardEmits>();

const coreStore = useCoreStore();
const statsigEventsStore = useStatsigEventsStore();
const browserStore = useBrowserStore();

function onGalleryImageSwipe(): void {
  statsigEventsStore.logTripOverviewActivityGalleryImageSwipe();
}

function onShowDetailsExperiment(): void {
  if (!props.shouldHideProductLink) {
    emit('show-details');
  }
}

function removeActivity(): void {
  statsigEventsStore.logExperimentIngredientRemoved('activity');
  emit('remove');
}
</script>

<template>
  <div class="activity-product-card">
    <ImgProductCardLayout
      v-if="!props.shouldHideProductLink"
      :enable-hover-color="!props.printMode"
    >
      <template #image>
        <AppSwiperGallery
          :image-urls="props.productCardData.imgSrcs"
          :show-nav-buttons="!props.printMode"
          :show-number-of-slides="!props.printMode"
          :display-gallery-dots="false"
          :allow-full-gallery="false"
          :error-image-handler="
            (src) =>
              coreStore.handleImageLoadError({
                src: src,
                source: 'activity-product-card-swiper-gallery',
              })
          "
          :print-mode="props.printMode"
          :full-gallery-aria-label="$t('a11y.open_image_gallery')"
          :left-arrow-aria-label="$t('a11y.previous_image')"
          :right-arrow-aria-label="$t('a11y.next_image')"
          @click="emit('show-details')"
          @gallery-image-swipe="onGalleryImageSwipe"
        />
      </template>
      <template #content>
        <ActivityProductCardContent
          :product-card-content="props.productCardData.productCardContent"
          :product-card-context="props.productCardContext"
          :should-hide-product-link="props.shouldHideProductLink"
          @show-details="emit('show-details')"
        />
      </template>
      <template #footer>
        <ProductCardFooter @show-details="emit('show-details')" />
      </template>
      <template #tag>
        <DateCardTag :date="props.date" />
      </template>
    </ImgProductCardLayout>

    <ActivityProductCardLayout
      v-else
      :should-hide-product-link="props.shouldHideProductLink"
    >
      <template #image>
        <AppSwiperGallery
          :image-urls="props.productCardData.imgSrcs"
          :show-nav-buttons="!props.printMode"
          :show-number-of-slides="!props.printMode"
          :display-gallery-dots="false"
          :allow-full-gallery="false"
          :error-image-handler="
            (src) =>
              coreStore.handleImageLoadError({
                src: src,
                source: 'activity-product-card-swiper-gallery',
              })
          "
          :print-mode="props.printMode"
          :full-gallery-aria-label="$t('a11y.open_image_gallery')"
          :left-arrow-aria-label="$t('a11y.previous_image')"
          :right-arrow-aria-label="$t('a11y.next_image')"
          @click="onShowDetailsExperiment"
          @gallery-image-swipe="onGalleryImageSwipe"
        />
      </template>
      <template #content>
        <div class="activity-product-card-content">
          <ActivityProductCardContent
            :product-card-content="props.productCardData.productCardContent"
            :product-card-context="props.productCardContext"
            :should-hide-product-link="props.shouldHideProductLink"
            @show-details="onShowDetailsExperiment"
            @remove="removeActivity"
          />
          <div
            v-if="!props.printMode && props.shouldHideProductLink"
            class="button-wrapper"
          >
            <AppRawButton
              class="details"
              :ariaLabel="$t('a11y.open_activity_page')"
              @click="emit('show-details')"
            >
              {{ $t('planner.details') }}
            </AppRawButton>
            <AppButton
              state="enabled"
              type="primary"
              size="small"
              :ariaLabel="$t('a11y.open_activity_page')"
              @click="emit('show-details-tickets')"
            >
              {{ $t('planner.edit') }}
            </AppButton>
          </div>
        </div>
      </template>

      <template #tag>
        <DateCardTag :date="props.date" />
      </template>
      <template #trash-icon>
        <TripOverviewTrashIconButton
          v-if="!props.printMode"
          :with-border="browserStore.isSmallerThan600px"
          :rounded="true"
          :ariaLabel="$t('a11y.remove_activity')"
          @remove="removeActivity"
        />
      </template>
    </ActivityProductCardLayout>
  </div>
</template>

<style scoped lang="scss">
.activity-product-card-content {
  display: flex;
  flex-direction: column;
  height: 100%;

  .button-wrapper {
    display: flex;
    justify-content: flex-end;
    padding-top: var(--spacing-12);
    align-items: center;
    gap: var(--spacing-16);
    @include for-less-or-equal-than-599-px {
      padding: var(--spacing-12) var(--spacing-16) 0 var(--spacing-16);
    }
    .details {
      display: flex;
      @include font-label-m-light;
      text-decoration: underline;
      cursor: pointer;
    }
  }
}
</style>
